/**
 * RepBuddy - client-side PDF text extraction.
 *
 * Mirrors the CSV/XLSX flow (CompanyEnrichPage): the file is parsed entirely in the browser and
 * only the extracted text is sent to the backend. The PDF bytes never leave the client.
 *
 * pdf.js runs its parser in a Web Worker. We point the worker at the copy that ships with the
 * library so extraction works offline with no CDN dependency; webpack 5 (react-scripts 5) turns
 * the `new URL(..., import.meta.url)` below into a bundled asset URL at build time.
 */
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

// A case-study one-pager or deck, not a book. Cap the work so a huge PDF can't hang the tab.
const MAX_PAGES = 30;

/**
 * Extract readable text from a PDF File/Blob.
 * @param {File|Blob} file
 * @returns {Promise<string>} concatenated page text (empty string if the PDF has no text layer,
 *          e.g. a scanned/image-only PDF).
 */
export async function extractPdfText(file) {
  const data = await file.arrayBuffer();
  const pdf  = await pdfjsLib.getDocument({ data }).promise;

  const pageCount = Math.min(pdf.numPages, MAX_PAGES);
  const parts = [];
  for (let i = 1; i <= pageCount; i++) {
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();
    const text    = content.items.map(it => it.str || '').join(' ').trim();
    if (text) parts.push(text);
  }

  return parts.join('\n\n').replace(/[ \t]+/g, ' ').trim();
}
